/**
 * Hover controls on a reference — the quick-view eye, a lookup's clear cross — take no room until
 * the pointer or focus is on the reference. Merely transparent they would still claim their width,
 * and the referenced name would be cut short for buttons nobody can see. `:focus-within` keeps them
 * reachable from the keyboard: tabbing into the field shows them before focus moves on to them.
 *
 * Kept apart from ReferencePeekPanel, which is loaded lazily.
 */

import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/** The class every quick-view eye carries, so the box around it can show and hide it. */
export const REF_PEEK_CLASS = 'nexo-ref-peek';

const IDLE = ':not(:hover):not(:focus-within)';

/**
 * For the box a reference renders in — a read-only field, a grid cell. `keepShown` holds the eye
 * while its peek panel is open.
 */
export function revealRefPeekSx(keepShown: boolean): SystemStyleObject<Theme> {
  return keepShown ? {} : { [`&${IDLE} .${REF_PEEK_CLASS}`]: { display: 'none' } };
}

/**
 * For a lookup's Autocomplete: hides the eye and the clear cross while the field is idle, and gives
 * the name back the room MUI reserves for the cross whenever the field holds a value — 26px of icon
 * and 4px of gap is MUI's room for the dropdown arrow alone, in the standard variant forms use.
 */
export function revealLookupControlsSx(keepShown: boolean): SystemStyleObject<Theme> {
  if (keepShown) return {};
  return {
    ...revealRefPeekSx(false),
    [`&${IDLE} .MuiAutocomplete-clearIndicator`]: { display: 'none' },
    [`&.MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon${IDLE} .MuiAutocomplete-inputRoot.MuiInput-root`]: {
      paddingRight: '30px',
    },
  };
}
